import { formatProductCategoriesGA4, formatProductPrice } from "../functions";

export const createListId = (listName) => {
  return listName
    .toLowerCase()
    ?.replace(/\s+/g, "_")
    ?.replace(/[^\w_]/g, "")
    ?.replace(/_+/g, "_");
};

export const pageSummaryInfo = (currentPage, pageLimit, totalProducts) => {
  const toProduct = Math.min(pageLimit * currentPage, totalProducts);
  const startFrom = currentPage - 1;
  let info = {
    from: pageLimit * startFrom + 1,
    to: toProduct > totalProducts ? totalProducts : toProduct,
    total: totalProducts,
  };

  return info;
};

export const alertMessage = (t, category, is3BO) => {
  let message = t("messages.info.fitment_info_selection_3bo", {
    category: category?.name,
  });
  if (!is3BO) {
    message = t("messages.info.fitment_info_selection", {
      category: category?.name,
    });
  }
  return message;
};

export const mapProducts = (
  productArr,
  isGrouped = false,
  extraInfo = {},
  bsIcon = "",
  recIcon = "",
) => {
  let items = {};
  productArr.forEach((p) => {
    const metaData = extraInfo[p.sku] || {};
    const extraVar = p?.extraVariable || {};
    const articleNo = p?.art_nr || "";
    const key = isGrouped ? articleNo : articleNo ? articleNo + p?.uid : p?.uid;

    if (!items[key]) {
      let smallImg = {};
      if (p?.small_image?.url) {
        smallImg = {
          ...p?.small_image,
          position: 0,
          slug: p?.small_image?.label + " 1",
        };
      }
      items[key] = {
        brands: [],
        small_image: smallImg,
        product_title: p?.name,
        bestseller_img: bsIcon,
        recommended_img: recIcon,
        article_number: articleNo,
        branch: metaData?.branch || {},
        description: p?.description || {},
        productOffer: p?.promo_offer || null,
        media_gallery: p?.media_gallery || [],
      };
    }

    const brand = extraVar?.brand_info || {};
    const isSelected = items[key]["brands"].length === 0;
    const minimumPrice = p?.price_range?.minimum_price || {};
    const amountOff = minimumPrice?.discount?.amount_off || 0;
    const percentOff = minimumPrice?.discount?.percent_off || 0;
    const finalPriceObj = minimumPrice?.final_price || {};
    const regularPriceObj = minimumPrice?.regular_price || {};

    items[key]["brands"].push({
      ...brand,
      sku: p.sku,
      fitment_info: [],
      product_id: p?.id,
      base_title: p?.name,
      product_uid: p?.uid,
      url_key: p?.url_key,
      quantity: p?.qty || 0,
      is_selected: isSelected,
      surcharge: p?.surcharge,
      review: metaData?.review,
      thumbnail: p?.thumbnail || {},
      special_price: p?.special_price,
      best_seller: p?.best_seller || 0,
      is_recommended: p?.is_recommended,
      showBtn: finalPriceObj?.value > 0,
      hub_stock: metaData?.hub_stock || 0,
      ndc_stock: metaData?.ndc_stock || 0,
      showDiscountedPrice: amountOff >= 5,
      productOffer: p?.promo_offer || null,
      sold_in_pairs: p?.sold_in_pairs || 0,
      amountOff_without_currency: amountOff,
      query_text: metaData?.query_text || "",
      currency: finalPriceObj?.currency || "",
      breadcrumbs: extraVar?.breadcrumbs || [],
      branch_stock: metaData?.branch_stock || 0,
      special_to_date: p?.special_to_date || "",
      special_from_date: p?.special_from_date || "",
      final_price_without_currency: finalPriceObj?.value,
      final_price: formatProductPrice(finalPriceObj || 0),
      regular_price_without_currency: regularPriceObj?.value,
      specialCoupon_applied: p?.specialCoupon_applied || true,
      regular_price: formatProductPrice(regularPriceObj || 0),
      stop_start_compatible: p?.stop_start_compatible || null,
      percent_off: percentOff > 9 ? `${percentOff}` : `0${percentOff}`,
      warranty_guaranteed_text: p?.warranty_guaranteed_text || null,
      amountOff: formatProductPrice({ ...regularPriceObj, value: amountOff }),
      showStrikePrice:
        finalPriceObj?.value < regularPriceObj?.value || !!p?.special_price,
      title_with_brand: `${brand?.title ? brand?.title?.toUpperCase() + " - " : ""
        } ${p?.name}`,
    });
  });

  let productList = Object.values(items);
  if (isGrouped) {
    productList = productList.map((p) => {
      let isSelected = false;
      let item = { ...p };

      // Sort products by final price
      item.brands.sort(
        (a, b) =>
          a.final_price_without_currency - b.final_price_without_currency,
      );

      // Select the brand with the lowest price and available quantity
      item.brands = item.brands.map((b) => {
        b.is_selected = false;
        if (!isSelected && b.quantity > 0) {
          b.is_selected = true;
          isSelected = true;
        }
        return b;
      });

      // Select the first brand if no brand is selected
      if (!isSelected) item.brands[0].is_selected = true;
      return item;
    });
  }
  return productList;
};

export const setToStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (err) {
    console.error(err);
  }
};

export const branchFormattedDetail = (branch) => {
  if (!branch) return null;
  return {
    name: branch?.name,
    type: branch?.type,
    email: branch?.email,
    is_hub: branch?.is_hub,
    is_NDC: branch?.is_NDC || false,
    address1: branch?.address1,
    address2: branch?.address2,
    address3: branch?.address3,
    address4: branch?.address4,
    store_id: branch?.store_id,
    postcode: branch?.postcode,
    latitude: branch?.latitude,
    distance: branch?.distance,
    branch_id: branch?.branch_id,
    hyperlink: branch?.hyperlink,
    longitude: branch?.longitude,
    telephone: branch?.telephone,
    closing_sun: branch?.closing_sun,
    closing_sat: branch?.closing_sat,
    open_sunday: branch?.open_sunday,
    opening_sun: branch?.opening_sun,
    opening_sat: branch?.opening_sat,
    holiday_info: branch?.holiday_info,
    open_saturday: branch?.open_saturday,
    closing_mon_fri: branch?.closing_mon_fri,
    opening_mon_fri: branch?.opening_mon_fri,
    title: `${branch?.type} ${branch?.name}`,
  };
};

export const filterSubCategories = (
  dataArr,
  vehicleInfo,
  showForthLevelView = false,
) => {
  if (vehicleInfo) {
    let filteredArr = [];
    let componentIds = [];
    let filterComponent = false;

    if (vehicleInfo?.vehicle && !vehicleInfo?.vehicle?.mvl_key) {
      filterComponent = true;
      componentIds = (vehicleInfo?.vehicle?.components || [])?.map((c) =>
        Number(c?.id),
      );
    }

    if (showForthLevelView) {
      dataArr.forEach((cat) => {
        if (filterComponent) {
          if (componentIds.includes(Number(cat.component_id))) {
            filteredArr.push(cat);
          }
        } else {
          filteredArr.push(cat);
        }
      });
    } else {
      dataArr.forEach((cat) => {
        const catObj = { name: cat.name, url_path: cat.url_path, children: [] };
        cat.children.forEach((subCat) => {
          if (filterComponent) {
            if (componentIds.includes(Number(subCat.component_id))) {
              catObj.children.push(subCat);
            }
          } else {
            catObj.children.push(subCat);
          }
        });
        if (catObj.children.length > 0) filteredArr.push(catObj);
      });
    }

    return filteredArr;
  }
  return dataArr;
};

export const formatProductListing = (products, fixQuantity) => {
  const formattedProducts = products.map((product, i) => {
    const brands = product?.brands || [];
    const brand = brands.find((b) => b.is_selected);
    return {
      item_id: brand?.sku,
      item_name: brand?.base_title || product?.product_title,
      discount: brand?.amountOff_without_currency,
      item_brand: brand?.title,
      price: brand?.final_price_without_currency,
      quantity: fixQuantity ? 1 : brand?.quantity,
      index: i,
      ...(Number(brand?.amountOff_without_currency) &&
        !brand?.specialCoupon_applied && {
        coupon: Cookies.get("specialCoupon"),
      }),
      ...formatProductCategoriesGA4(brand?.breadcrumbs),
    };
  });

  return formattedProducts;
};

export const createItemListPayload = (
  products,
  listName,
  isFormatted = false,
  fixQuantity = false
) => {
  let obj = {
    item_list_id: createListId(listName),
    item_list_name: listName,
    items: isFormatted ? products : formatProductListing(products, fixQuantity),
  };

  return obj;
};

export const updateMetaInfo = (p) => {
  let title = document.title;
  let descTag = document.querySelector("meta[name='description']");
  let desc = descTag?.content;
  const pageRegex = / - Page \d+/g;
  if (p > 1) {
    title = title.match(pageRegex)
      ? title.replace(pageRegex, ` - Page ${p}`)
      : title.replace(" |", ` - Page ${p} |`);

    desc = desc.replace(pageRegex, "") + ` - Page ${p}`;
  } else {
    title = title.replace(pageRegex, "");
    desc = desc.replace(pageRegex, "");
  }

  document.title = title;
  if (descTag) {
    descTag.setAttribute("content", desc);
  }
  const ogTitle = document.querySelector("meta[property='og:title']");
  if (ogTitle) {
    ogTitle.setAttribute("content", title);
  }
  const ogDesc = document.querySelector("meta[property='og:description']");
  if (ogDesc) {
    ogDesc.setAttribute("content", desc);
  }
  const tTitle = document.querySelector("meta[name='twitter:title']");
  if (tTitle) {
    tTitle.setAttribute("content", title);
  }
  const tDesc = document.querySelector("meta[name='twitter:description']");
  if (tDesc) {
    tDesc.setAttribute("content", desc);
  }
};
